import React, { memo } from "react";
import { withNamespaces } from "react-i18next";
import { Alert, Button, Spinner } from "reactstrap";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import ReportIncidenService from "../../components/api/ReportIncidenService";

const EmailDomainMismatchAlert = ({ t, report, compact = false }) => {
	const proof = report?.email_domain_proof;

	const {
		mutate: downloadProofMutation,
		isLoading: proofIsDownloading,
	} = useMutation({
		mutationKey: [
			"data-protection-dsr-download-email-domain-proof",
			proof?.attachment_id,
		],
		mutationFn: async () => {
			const service = ReportIncidenService.getInstance();
			return await service.downloadReportAttachment(proof.attachment_id);
		},
		onSuccess: (blobData) => {
			const url = window.URL.createObjectURL(
				new Blob([blobData], { type: "*" })
			);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", proof?.name || "proof");
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		},
		onError: () => {
			toast(t("An error occurred while downloading the file."), {
				type: "error",
			});
		},
	});

	if (!report?.email_domain_mismatch || report?.requester_type === "private_person") {
		return null;
	}

	return (
		<Alert color="warning" className={compact ? "mt-1 mb-0" : "mb-0"}>
			<h6 className="alert-heading mb-2">
				{t("Email domain does not match organization website")}
			</h6>

			{!compact && (
				<>
					<p className="mb-1">
						<strong>{t("Organization")}:</strong>{" "}
						{report?.organization_name || "-"}
					</p>
					<p className="mb-1">
						<strong>{t("Website")}:</strong>{" "}
						{report?.organization_website || "-"}
					</p>
				</>
			)}

			<p className={proof?.attachment_id ? "mb-2" : "mb-0"}>
				{t(
					"Reporter may have uploaded proof that this email belongs to the company."
				)}
			</p>

			{proof?.attachment_id && (
				<div className="d-flex flex-wrap align-items-center gap-2">
					<span>
						{t("This is the proof file:")} {proof?.name}
					</span>
					<Button
						color="warning"
						outline
						size="sm"
						type="button"
						disabled={proofIsDownloading}
						onClick={() => downloadProofMutation()}
					>
						{proofIsDownloading && (
							<Spinner size="sm" className="me-1" />
						)}
						{t("Download")}
					</Button>
				</div>
			)}
		</Alert>
	);
};

export default withNamespaces()(memo(EmailDomainMismatchAlert));
